import React, { useEffect, useState } from 'react';
import { API_BASE, authHeaders } from '../../utils/api';
import '../../styles/common.css';
import '../placement/TopRecruiters.css';
import './Gallery.css';

const AdminGallery = () => {
  const [form, setForm] = useState({
    title: '',
    from_date: '',
    to_date: '',
    meta_tag: '',
    images: []
  });
  const [gallery, setGallery] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userRole, setUserRole] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewingGallery, setViewingGallery] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserRole(payload.role || '');
      } catch (e) {
        console.error('Error parsing token', e);
      }
    }
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/gallery`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setGallery(data.data);
    } catch (err) {
      console.error(err);
      setError('Failed to load gallery');
    }
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = e => {
    const files = Array.from(e.target.files);
    setForm(prev => ({ ...prev, images: files }));
  };

  const removeImage = (index) => {
    const newImages = form.images.filter((_, i) => i !== index);
    setForm(prev => ({ ...prev, images: newImages }));
  };

  const resetForm = () => {
    setForm({ title: '', from_date: '', to_date: '', meta_tag: '', images: [] });
    setEditingId(null);
    setShowForm(false);
    setError('');
    setSuccess('');
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!form.title) {
      setError('Title is required');
      setTimeout(() => setError(''), 3000);
      return;
    }

    const fd = new FormData();
    fd.append('title', form.title);
    fd.append('from_date', form.from_date);
    fd.append('to_date', form.to_date);
    fd.append('meta_tag', form.meta_tag);

    form.images.forEach(file => fd.append('gallery_images', file));

    try {
      const url = editingId
        ? `${API_BASE}/api/admin/gallery/${editingId}`
        : `${API_BASE}/api/admin/gallery`;
      const method = editingId ? 'PUT' : 'POST';

      const headers = authHeaders();
      delete headers['Content-Type']; // Let browser set Content-Type for FormData

      const res = await fetch(url, {
        method,
        headers,
        body: fd
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error');

      setSuccess(editingId ? 'Gallery updated successfully' : 'Gallery created and sent for approval');
      setTimeout(() => setSuccess(''), 3000);
      resetForm();

      if (editingId) {
        // Force page reload after editing to show updated status
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        fetchGallery();
      }
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleApprove = async id => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/gallery/${id}/approve`, {
        method: 'POST',
        headers: authHeaders()
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error');
      setSuccess('Gallery approved successfully');
      setTimeout(() => setSuccess(''), 3000);

      // Update the gallery state immediately
      setGallery(prev => prev.map(item =>
        item.id === id ? { ...item, status: 'approved' } : item
      ));

      fetchGallery();
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleReject = async id => {
    if (!window.confirm('Are you sure you want to reject this gallery?')) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/gallery/${id}/reject`, {
        method: 'POST',
        headers: authHeaders()
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error');
      setSuccess('Gallery rejected successfully');
      setTimeout(() => setSuccess(''), 3000);
      fetchGallery();
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleEdit = item => {
    setForm({
      title: item.title,
      from_date: item.from_date ? item.from_date.split('T')[0] : '',
      to_date: item.to_date ? item.to_date.split('T')[0] : '',
      meta_tag: item.meta_tag || '',
      images: []
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async id => {
    if (!window.confirm('Are you sure you want to delete this gallery? This action cannot be undone.')) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/gallery/${id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error');
      setSuccess('Gallery deleted successfully');
      setTimeout(() => setSuccess(''), 3000);
      fetchGallery();
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(''), 3000);
    }
  };

  return (
    <div className="admin-page top-recruiters-page">
      <div className="page-heading">
        <h1>Gallery Management</h1>
      </div>

      {error && <div className="admin-alert error">{error}</div>}
      {success && <div className="admin-alert success">{success}</div>}



      <div className="recruiters-grid" style={{ gridTemplateColumns: '400px 1fr' }}>
        <div className="card" style={{ maxHeight: '600px', overflowY: 'auto' }}>
          <h2>{editingId ? 'Edit Gallery' : 'Add Gallery'}</h2>
          <form className="recruiter-form" onSubmit={handleSubmit}>
            <label>
              Title *
              <input name="title" value={form.title} onChange={handleChange} required />
            </label>
            <label>
              From Date
              <input type="date" name="from_date" value={form.from_date} onChange={handleChange} />
            </label>
            <label>
              To Date
              <input type="date" name="to_date" value={form.to_date} onChange={handleChange} />
            </label>
            <label>
              Meta Tag
              <input name="meta_tag" value={form.meta_tag} onChange={handleChange} />
            </label>
            <label>
              Images
              <input type="file" accept="image/*" multiple onChange={handleFileChange} required={!editingId} />
            </label>
            {form.images.length > 0 && (
              <div className="image-preview-section">
                <h4>Selected Images ({form.images.length})</h4>
                <div className="image-preview-grid">
                  {form.images.map((file, index) => (
                    <div key={index} className="image-preview-item">
                      <img src={URL.createObjectURL(file)} alt={`Preview ${index + 1}`} />
                      <button type="button" className="remove-image-btn" onClick={() => removeImage(index)}>✕</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {editingId ? 'Update' : 'Create'}
              </button>

            </div>
          </form>
        </div>

        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h2>Gallery List</h2>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px' }}>
              <option value="all">All Galleries</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Period</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {gallery.filter(item => statusFilter === 'all' || item.status === statusFilter).map(item => (
                <tr key={item.id}>
                  <td>{item.title}</td>
                  <td>{item.from_date && item.to_date ? `${new Date(item.from_date).toLocaleDateString()} - ${new Date(item.to_date).toLocaleDateString()}` : 'N/A'}</td>
                  <td><span className={`status status-${item.status}`}>{item.status}</span></td>
                  <td className="actions">
                    <button className="btn-edit" onClick={() => handleEdit(item)}>✏️ Edit</button>
                    <button className="btn-delete" onClick={() => handleDelete(item.id)}>🗑️ Delete</button>
                    {item.status !== 'approved' && userRole === 'superadmin' && <button className="btn-edit" onClick={() => handleApprove(item.id)}>✅ Approve</button>}
                    {item.status !== 'rejected' && item.status !== 'approved' && userRole === 'superadmin' && <button className="btn-delete" onClick={() => handleReject(item.id)}>❌ Reject</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* View Gallery Modal */}
      {viewingGallery && (
        <div className="modal-overlay" onClick={() => setViewingGallery(null)}>
          <div className="modal-content view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{viewingGallery.title}</h2>
              <button className="close-btn" onClick={() => setViewingGallery(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="gallery-info">
                <p><strong>Period:</strong> {new Date(viewingGallery.from_date).toLocaleDateString()} - {new Date(viewingGallery.to_date).toLocaleDateString()}</p>
                <p><strong>Status:</strong> <span className={`status-badge ${viewingGallery.status}`}>{viewingGallery.status}</span></p>
                {viewingGallery.meta_tag && <p><strong>Tags:</strong> {viewingGallery.meta_tag}</p>}
                <p><strong>Images:</strong> {Array.isArray(viewingGallery.gallery_image) ? viewingGallery.gallery_image.length : 0} files</p>
              </div>
              <div className="view-images">
                {Array.isArray(viewingGallery.gallery_image) && viewingGallery.gallery_image.length > 0 ? (
                  viewingGallery.gallery_image.map((img, i) => (
                    <img key={i} src={`${API_BASE}${img}`} alt={`Gallery ${i + 1}`} className="view-image" />
                  ))
                ) : (
                  <p className="no-images-modal">No images available</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminGallery;